import { useEffect, useRef, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useMutation } from "@tanstack/react-query"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import { useAccountId } from "resources/account"
import { log } from "helpers/logger"

const CLEAR_PREFERENCES_PARAM = "clearPreferences"
const PREFERENCE_KEY = "dashboard"
const PREFERENCE_TYPE = "dashboard"

// List of all dashboard preference subtypes that need to be cleared
const DASHBOARD_PREFERENCE_SUBTYPES = [
  "dashboard",
  "asset-view",
  "kpi",
  "factors-view",
  "graph",
  "modules",
  "dockviewLayout",
]

/**
 * Hook that clears all dashboard preferences when the URL contains the clearPreferences parameter
 *
 * This hook should be used at the top level of the dashboard, BEFORE any preference loading occurs.
 * When ?clearPreferences is detected (with or without a value):
 * 1. All dashboard preferences are set to empty objects
 * 2. The page reloads with the clearPreferences param removed
 *
 * @returns {{ isClearing: boolean }} - Whether preferences are currently being cleared
 */
export function useClearDashboardPreferences() {
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const accountId = useAccountId()
  const hasClearedRef = useRef(false)

  // Check if the param exists (with or without a value)
  // Both ?clearPreferences and ?clearPreferences=true will trigger clearing
  const shouldClear = searchParams.has(CLEAR_PREFERENCES_PARAM)

  const removeParamAndReload = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete(CLEAR_PREFERENCES_PARAM)
    window.location.replace(url.toString())
  }, [])

  const clearPreferencesMutation = useMutation({
    mutationFn: async () => {
      // Clear all dashboard preference subtypes
      const clearPromises = DASHBOARD_PREFERENCE_SUBTYPES.map((subType) => {
        const payload = {
          items: [{
            accountId,
            key: PREFERENCE_KEY,
            type: `${PREFERENCE_TYPE}.${subType}`,
            value: {}, // Empty object clears the preferences
          }],
        }
        return new Promise((resolve, reject) => {
          dispatch(ApiWrapperCreators.put("/api/users/me/preferences", payload, resolve, reject))
        })
      })

      return Promise.all(clearPromises)
    },
    onSuccess: () => {
      removeParamAndReload()
    },
    onError: (error) => {
      log(error)
      // Still remove the param and reload to prevent infinite loop
      removeParamAndReload()
    },
  })

  useEffect(() => {
    // Only clear once, and only if the param is present
    if (!shouldClear || hasClearedRef.current || !accountId) {
      return
    }

    hasClearedRef.current = true
    clearPreferencesMutation.mutate()
  }, [shouldClear, accountId, clearPreferencesMutation])

  return {
    isClearing: shouldClear,
  }
}
